/**
 * usePopupDataState - Manages popup data with "stale while revalidate" pattern
 *
 * This hook provides a consistent pattern for popup components that:
 * 1. Show existing data while fetching new data
 * 2. Display a subtle loading indicator during updates
 * 3. Handle initial loading vs subsequent updates differently
 *
 * Loading indicator behavior:
 * - Only shows after a delay (200ms) to avoid flickering for fast fetches
 * - Once shown, displays for minimum duration (500ms) for consistent UX
 *
 * @example
 * const {
 *   data,
 *   isInitialLoading,
 *   isRefreshing,
 *   setData,
 *   setRefreshing,
 * } = usePopupDataState<MyDataType>();
 *
 * // In message handler:
 * if (msg.type === MessageType.POPUP_INIT_RESPONSE) {
 *   setData(msg.payload);
 * }
 * if (msg.type === MessageType.DATA_UPDATE) {
 *   setRefreshing(true); // Show loading indicator after delay
 *   // ... then when data arrives:
 *   setData(newData);
 * }
 */
import { useState, useCallback, useRef } from 'react';

/** Delay before showing refresh indicator (ms) - avoids flicker for fast fetches */
const REFRESH_INDICATOR_DELAY = 200;
/** Minimum time to show indicator once visible (ms) - consistent UX */
const MIN_REFRESH_DISPLAY_TIME = 500;

export interface PopupDataState<T> {
  /** The current data (may be stale while refreshing) */
  data: T | null;
  /** True only during initial load (no data yet) */
  isInitialLoading: boolean;
  /** True when fetching new data while showing existing data */
  isRefreshing: boolean;
  /** Error message if any */
  error: string | null;
  /** Update the data and clear loading states */
  setData: (data: T) => void;
  /** Set the refreshing state (call before expecting new data) */
  setRefreshing: (refreshing: boolean) => void;
  /** Set error state */
  setError: (error: string | null) => void;
  /** Reset to initial loading state */
  reset: () => void;
}

export function usePopupDataState<T>(): PopupDataState<T> {
  const [data, setDataInternal] = useState<T | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setErrorInternal] = useState<string | null>(null);

  // Track if we've ever received data
  const hasReceivedData = useRef(false);
  // Track when indicator became visible for minimum display time
  const indicatorVisibleTime = useRef<number | null>(null);
  // Timer for delayed indicator show
  const showIndicatorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Store pending data if we need to delay showing it
  const pendingData = useRef<T | null>(null);

  const clearTimers = useCallback(() => {
    if (showIndicatorTimer.current) {
      clearTimeout(showIndicatorTimer.current);
      showIndicatorTimer.current = null;
    }
  }, []);

  const setData = useCallback(
    (newData: T) => {
      hasReceivedData.current = true;
      setIsInitialLoading(false);
      setErrorInternal(null);

      // Clear any pending show timer
      clearTimers();

      // If indicator is visible, ensure minimum display time
      if (indicatorVisibleTime.current !== null) {
        const elapsed = Date.now() - indicatorVisibleTime.current;
        const remaining = MIN_REFRESH_DISPLAY_TIME - elapsed;

        if (remaining > 0) {
          // Store data and delay update
          pendingData.current = newData;
          setTimeout(() => {
            setDataInternal(pendingData.current as T);
            setIsRefreshing(false);
            indicatorVisibleTime.current = null;
            pendingData.current = null;
          }, remaining);
          return;
        }
      }

      // No delay needed
      setDataInternal(newData);
      setIsRefreshing(false);
      indicatorVisibleTime.current = null;
    },
    [clearTimers],
  );

  const setRefreshing = useCallback(
    (refreshing: boolean) => {
      // Only set refreshing if we have data (otherwise it's still initial loading)
      if (!hasReceivedData.current) return;

      if (refreshing) {
        // Delay showing the indicator
        clearTimers();
        showIndicatorTimer.current = setTimeout(() => {
          indicatorVisibleTime.current = Date.now();
          setIsRefreshing(true);
        }, REFRESH_INDICATOR_DELAY);
      } else {
        clearTimers();
        setIsRefreshing(false);
        indicatorVisibleTime.current = null;
      }
    },
    [clearTimers],
  );

  const setError = useCallback(
    (err: string | null) => {
      clearTimers();
      setErrorInternal(err);
      setIsInitialLoading(false);
      setIsRefreshing(false);
      indicatorVisibleTime.current = null;
    },
    [clearTimers],
  );

  const reset = useCallback(() => {
    clearTimers();
    hasReceivedData.current = false;
    indicatorVisibleTime.current = null;
    pendingData.current = null;
    setDataInternal(null);
    setIsInitialLoading(true);
    setIsRefreshing(false);
    setErrorInternal(null);
  }, [clearTimers]);

  return {
    data,
    isInitialLoading,
    isRefreshing,
    error,
    setData,
    setRefreshing,
    setError,
    reset,
  };
}
